/**
 * App initialization loading screen
 *
 * Shows branded loading screen with task checklist during app startup.
 * Each loading task displays a spinner while loading, then a checkmark when complete.
 */
import type { ReactElement } from 'react';

import Spinner from '@/components/Spinner/Spinner';

export type LoadingTaskStatus = 'pending' | 'loading' | 'complete' | 'error';

export interface LoadingTask {
  id: string;
  label: string;
  status: LoadingTaskStatus;
  error?: string;
}

export interface AppLoaderProps {
  tasks: LoadingTask[];
  error?: string | null;
  onRetry?: () => void;
}

function TaskIcon({ status }: { status: LoadingTaskStatus }): ReactElement {
  switch (status) {
    case 'loading':
      return <Spinner size="sm" />;
    case 'complete':
      return (
        <svg className="h-5 w-5 text-emerald-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'error':
      return (
        <svg className="text-danger h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
            clipRule="evenodd"
          />
        </svg>
      );
    default:
      // pending
      return <div className="bg-ash/30 h-5 w-5 rounded-full" aria-hidden="true" />;
  }
}

function TaskItem({ task }: { task: LoadingTask }): ReactElement {
  const textColor =
    task.status === 'complete'
      ? 'text-silver'
      : task.status === 'error'
        ? 'text-danger'
        : task.status === 'loading'
          ? 'text-pearl'
          : 'text-ash';

  return (
    <li className="flex items-center gap-3">
      <div className="flex h-6 w-6 items-center justify-center">
        <TaskIcon status={task.status} />
      </div>
      <span className={`text-sm ${textColor}`}>{task.label}</span>
      {task.error && <span className="text-danger/70 text-xs">— {task.error}</span>}
    </li>
  );
}

export default function AppLoader({ tasks, error, onRetry }: AppLoaderProps): ReactElement {
  const hasError = error || tasks.some((t) => t.status === 'error');

  return (
    <div className="bg-void fixed inset-0 z-50 flex flex-col items-center justify-center">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="h-full w-full"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, var(--color-aether) 0%, transparent 50%),
                            radial-gradient(circle at 75% 75%, var(--color-mythic) 0%, transparent 50%)`,
          }}
        />
      </div>

      <div className="relative z-10 flex flex-col items-center">
        {/* Logo / Brand */}
        <div className="mb-8">
          <h1 className="font-heading text-pearl text-4xl font-bold tracking-wider">
            Mtg<span className="text-aether">Craft</span>
          </h1>
        </div>

        {/* Task list */}
        <div className="bg-obsidian/50 mb-6 min-w-72 rounded-xl border border-white/5 p-6">
          <ul className="space-y-3" role="list" aria-label="Loading tasks">
            {tasks.map((task) => (
              <TaskItem key={task.id} task={task} />
            ))}
          </ul>
        </div>

        {hasError ? (
          <>
            {/* Error state */}
            <div className="bg-danger/10 border-danger/30 mb-6 max-w-md rounded-xl border p-4 text-center">
              <p className="text-danger text-sm font-medium">Failed to initialize application</p>
              {error && <p className="text-danger/70 mt-1 text-xs">{error}</p>}
            </div>
            {onRetry && (
              <button
                onClick={onRetry}
                className="bg-aether hover:bg-aether-dim rounded-full px-6 py-2 font-medium text-white transition-colors"
              >
                Retry
              </button>
            )}
          </>
        ) : (
          /* Subtle hint */
          <p className="text-ash/60 mt-4 max-w-xs text-center text-xs">
            Loading your collection data. This may take a moment on first load.
          </p>
        )}
      </div>
    </div>
  );
}
